import type {
  OpsTrafficBreakdownRow,
  OpsTrafficBucket,
  OpsTrafficMetrics,
  OpsTrafficOverviewResponse,
  OpsTrafficRisk,
} from '@nadoutong/contracts';
import type { ReactNode } from 'react';
import { useState } from 'react';
import {
  Calendar,
  CalendarClock,
  CalendarRange,
  Clock,
  ExternalLink,
  Loader2,
  PauseCircle,
  RefreshCw,
  SearchX,
  SlidersHorizontal,
  Timer,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  useOpsTrafficOverview,
  type OpsTrafficTrafficClass,
  type OpsTrafficWindow,
} from '../api/traffic.ts';
import { formatCompact, formatLocalTimestamp, formatUsdCents, formatUsdMicrosOrCents } from '../components/formatters.ts';
import { CustomerCell } from '../components/identity/CustomerCell.tsx';
import { LocalDateTimeInput } from '../components/LocalDateTimeInput.tsx';
import { USAGE_WINDOW_UNAVAILABLE_HINT, usageWindowUnavailable } from '../components/pricingErrors.ts';
import { RiskItemCard } from '../components/risk.tsx';
import type { RiskCardItem } from '../components/risk.tsx';
import {
  attributableFigures,
  errorSeverity,
  fallbackSeverity,
  slowFirstByteSeverity,
} from '../components/trafficHealth.ts';
import type { TrafficSeverity } from '../components/trafficHealth.ts';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Chip,
  EmptyState,
  KpiCard,
  PageHeader,
  Panel,
  Select,
  StatusLamp,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
  buttonClass,
} from '../ui/index.ts';
import type { KpiTone } from '../ui/index.ts';

type TrafficTab = 'supply' | 'customers' | 'products';

/**
 * The tone vocabulary this page renders in. Identical to
 * `TrafficSeverity` — the thresholds that produce it moved to
 * `components/trafficHealth.ts` (shared with 系统健康透视), and this
 * alias keeps the ~20 local prop signatures reading in page terms.
 */
type MetricTone = TrafficSeverity;

const INTEGER_FORMATTER = new Intl.NumberFormat('en-US');

const WINDOW_OPTIONS: readonly OpsTrafficWindow[] = ['5m', '1h', '24h', '7d', 'custom'];
const TRAFFIC_CLASS_OPTIONS: readonly OpsTrafficTrafficClass[] = ['tenant', 'probe', 'all'];
const TAB_OPTIONS: readonly { key: TrafficTab; label: string; testId: string }[] = [
  { key: 'supply', label: '上游供给', testId: 'traffic-tab-supply' },
  { key: 'customers', label: '客户流量', testId: 'traffic-tab-customers' },
  { key: 'products', label: '产品渠道', testId: 'traffic-tab-products' },
];

export function TrafficMonitorPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  // Off by default (#2278): every poll is a full overview materialisation on
  // the production database. Operators opt in per visit.
  const [autoRefresh, setAutoRefresh] = useState(false);
  const window = parseWindow(params.get('window'));
  const trafficClass = parseTrafficClass(params.get('trafficClass'));
  const tab = parseTab(params.get('tab'));
  const from = window === 'custom' ? params.get('from') ?? undefined : undefined;
  const to = window === 'custom' ? params.get('to') ?? undefined : undefined;
  const query = useOpsTrafficOverview(
    { window, trafficClass, from, to },
    { autoRefresh },
  );

  function setWindow(next: OpsTrafficWindow): void {
    const clone = new URLSearchParams(params);
    clone.set('window', next);
    if (next !== 'custom') {
      clone.delete('from');
      clone.delete('to');
    }
    setParams(clone, { replace: true });
  }

  function setCustomBound(key: 'from' | 'to', value: string): void {
    const clone = new URLSearchParams(params);
    clone.set('window', 'custom');
    if (value.length > 0) clone.set(key, value);
    else clone.delete(key);
    setParams(clone, { replace: true });
  }

  function setTrafficClass(next: OpsTrafficTrafficClass): void {
    const clone = new URLSearchParams(params);
    clone.set('trafficClass', next);
    setParams(clone, { replace: true });
  }

  function setTab(next: TrafficTab): void {
    const clone = new URLSearchParams(params);
    clone.set('tab', next);
    setParams(clone, { replace: true });
  }

  return (
    <section className="flex min-w-0 flex-col gap-6" data-testid="traffic-monitor-page">
      <header className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div className="min-w-0">
          <p className="m-0 font-mono text-label text-paper-low">Realtime traffic</p>
          <PageHeader className="mt-1.5" title="流量监控" />
          <p className="m-0 mt-2 max-w-4xl text-dense text-paper-low">
            以 lifecycle 为主线看请求、错误、首包、上游供给、客户流量和产品渠道；请求日志与 trace 继续作为钻取目标。
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line-soft bg-card px-3 py-2 text-dense text-paper-low">
          <span aria-hidden="true" className="inline-flex">
            <StatusLamp tone={autoRefresh ? 'ok' : 'off'} size="sm" pulse={autoRefresh} />
          </span>
          <span className="font-mono text-label text-paper">
            {autoRefresh ? '60s refresh' : 'manual'}
          </span>
          <span className="font-mono">
            {query.data === undefined ? 'waiting' : formatLocalTimestamp(query.data.window.refreshedAt, { seconds: true })}
          </span>
        </div>
      </header>

      <TrafficControls
        window={window}
        trafficClass={trafficClass}
        from={params.get('from') ?? ''}
        to={params.get('to') ?? ''}
        autoRefresh={autoRefresh}
        loading={query.isFetching}
        onWindow={setWindow}
        onTrafficClass={setTrafficClass}
        onCustomBound={setCustomBound}
        onAutoRefresh={() => setAutoRefresh((value) => !value)}
        onRefresh={() => void query.refetch()}
      />

      {query.isLoading ? (
        <div className="flex items-center gap-3 rounded-lg border border-line-soft bg-card p-5" data-testid="traffic-monitor-loading">
          <span aria-hidden="true" className="rounded-md bg-raised p-2 text-signal">
            <Loader2 className="h-5 w-5 animate-spin" />
          </span>
          <div>
            <p className="m-0 text-body font-medium text-paper">正在加载流量监控</p>
            <p className="m-0 mt-1 text-dense text-paper-low">从 gateway lifecycle 与 usage logs 聚合准实时窗口。</p>
          </div>
        </div>
      ) : query.isError ? (
        <div className="rounded-lg border border-bad-line bg-bad-dim p-5 text-body text-bad-bright" data-testid="traffic-monitor-error">
          无法加载流量监控：{usageWindowUnavailable(query.error) ? USAGE_WINDOW_UNAVAILABLE_HINT : null}{query.error instanceof Error ? query.error.message : 'unknown error'}
        </div>
      ) : query.data === undefined || query.data.summary.requestCount === 0 ? (
        <EmptyState
          testId="traffic-monitor-empty"
          icon={<SearchX aria-hidden="true" className="h-5 w-5" />}
          title="当前窗口没有流量"
          hint="可以切换到 probe/all，或者放宽时间窗口后再看。"
        />
      ) : (
        <TrafficReady data={query.data} tab={tab} onTab={setTab} />
      )}
    </section>
  );
}

function TrafficControls({
  window,
  trafficClass,
  from,
  to,
  autoRefresh,
  loading,
  onWindow,
  onTrafficClass,
  onCustomBound,
  onAutoRefresh,
  onRefresh,
}: {
  readonly window: OpsTrafficWindow;
  readonly trafficClass: OpsTrafficTrafficClass;
  readonly from: string;
  readonly to: string;
  readonly autoRefresh: boolean;
  readonly loading: boolean;
  readonly onWindow: (value: OpsTrafficWindow) => void;
  readonly onTrafficClass: (value: OpsTrafficTrafficClass) => void;
  readonly onCustomBound: (key: 'from' | 'to', value: string) => void;
  readonly onAutoRefresh: () => void;
  readonly onRefresh: () => void;
}): JSX.Element {
  return (
    <div className="flex max-w-full flex-wrap items-end gap-3 rounded-lg border border-line-soft bg-card px-3 py-3">
      <div className="flex items-center gap-2">
        <SlidersHorizontal aria-hidden="true" className="h-4 w-4 shrink-0 text-signal" />
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="traffic window">
          {WINDOW_OPTIONS.map((item) => {
            const active = window === item;
            const Icon = trafficWindowIcon(item);
            return (
              <Button
                key={item}
                aria-pressed={active}
                variant={active ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => onWindow(item)}
                data-testid={`traffic-window-${item}`}
              >
                <Icon aria-hidden="true" className="h-3.5 w-3.5" />
                {item === 'custom' ? '自定义' : item}
              </Button>
            );
          })}
        </div>
      </div>
      <LocalDateTimeInput
        label="From"
        value={from}
        onChange={(value) => onCustomBound('from', value)}
        inputClassName="h-9 w-48"
        testId="traffic-from-filter"
      />
      <LocalDateTimeInput
        label="To"
        value={to}
        onChange={(value) => onCustomBound('to', value)}
        inputClassName="h-9 w-48"
        testId="traffic-to-filter"
      />
      <div className="flex flex-col gap-1.5">
        <span className="text-label text-paper-mid">Traffic</span>
        <Select
          value={trafficClass}
          onValueChange={(value) => onTrafficClass(parseTrafficClass(value))}
          ariaLabel="traffic class"
          testId="traffic-class-filter"
          className="w-32"
          options={TRAFFIC_CLASS_OPTIONS.map((item) => ({ value: item, label: trafficClassLabel(item) }))}
        />
      </div>
      <Button
        variant={autoRefresh ? 'secondary' : 'ghost'}
        aria-pressed={autoRefresh}
        onClick={onAutoRefresh}
        data-testid="traffic-auto-refresh"
      >
        {autoRefresh ? (
          <RefreshCw aria-hidden="true" className="h-3.5 w-3.5" />
        ) : (
          <PauseCircle aria-hidden="true" className="h-3.5 w-3.5" />
        )}
        Auto
      </Button>
      <Button variant="secondary" loading={loading} onClick={onRefresh}>
        刷新
      </Button>
    </div>
  );
}

function TrafficReady({
  data,
  tab,
  onTab,
}: {
  readonly data: OpsTrafficOverviewResponse;
  readonly tab: TrafficTab;
  readonly onTab: (tab: TrafficTab) => void;
}): JSX.Element {
  return (
    <>
      <KpiStrip summary={data.summary} />
      <RefusalSummaryLine summary={data.summary} />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <TrendPanel buckets={data.trend} />
        <div className="flex flex-col gap-6">
          <CacheWindowPanel windows={data.cacheWindows} />
          <RiskQueue risks={data.risks} window={data.window} />
        </div>
      </div>
      <DimensionWorkbench data={data} tab={tab} onTab={onTab} />
    </>
  );
}

function KpiStrip({ summary }: { readonly summary: OpsTrafficMetrics }): JSX.Element {
  const tokenTotal = summary.inputTokens + summary.outputTokens;
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-9">
      <MetricCard label="请求数" value={formatInteger(summary.requestCount)} sub={`成功 ${formatInteger(summary.successCount)} · 错误 ${formatInteger(summary.errorCount)}`} />
      {/* Net of gate refusals: a request the router itself refused never
          reached supply, so counting it here paints an outage where the
          admission gate was working. Production 2026-09-01 had one SKU at a
          76% "error rate" of which 99.8% was one tenant being refused. */}
      <NetErrorCard summary={summary} />
      {/* Tenant-final 429s. The pool 429s the retry ladder absorbed are a
          different population (#2240) and are named in the sub-label so a
          quiet card cannot be read as "the pool never rate-limited us". */}
      <MetricCard label="429" value={formatInteger(summary.rateLimitedCount)} sub={rateLimitedSubLabel(summary)} tone={summary.rateLimitedCount > 0 ? 'warning' : 'normal'} />
      <MetricCard label="首包均值" value={formatDuration(summary.avgFirstByteMs)} sub={`慢请求 ${formatRate(summary.slowFirstByteRate)}`} tone={summary.slowFirstByteRate >= 0.15 ? 'critical' : summary.slowFirstByteRate >= 0.05 ? 'warning' : 'normal'} />
      <MetricCard label="缓存命中" value={formatRate(summary.cacheRequestHitRate)} sub={`token ${formatRate(summary.cacheTokenHitRate)} · R/W ${formatRatio(summary.cacheReadWriteRatio)}`} />
      <MetricCard label="总耗时" value={formatDuration(summary.avgTotalMs)} sub="client observed" />
      <MetricCard label="上游耗时" value={formatDuration(summary.avgUpstreamMs)} sub="upstream phase" />
      <MetricCard label="Fallback" value={formatRate(summary.fallbackRate)} sub={`${formatInteger(summary.fallbackCount)} attempts`} tone={summary.fallbackRate >= 0.2 ? 'critical' : summary.fallbackRate >= 0.05 ? 'warning' : 'normal'} />
      {/* #1008 currency consistency: traffic is an on-call triage tool whose
          own design doc says "not business reporting". It deliberately keeps
          raw gateway USD cost (sub-cent micro precision) rather than the CNY
          business-display convention (#1001) used by its /customers/requests
          drill target — so the cost is labeled "USD" explicitly to make the
          currency switch on drill unambiguous. */}
      <MetricCard label="Tokens / Cost (USD)" value={formatCompact(tokenTotal)} sub={formatUsdMicrosOrCents(summary.costUsdMicros, summary.costUsdCents, false)} />
    </div>
  );
}

/**
 * How much of this window's error count is the router refusing requests, and
 * under which codes.
 *
 * Renders nothing when the gateway did not report the counter — absence means
 * "cannot report", so claiming a zero here would be inventing a fact.
 */
function RefusalSummaryLine({ summary }: { readonly summary: OpsTrafficMetrics }): JSX.Element | null {
  const refused = summary.refusedBeforeUpstreamCount;
  if (refused === undefined) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-line-soft bg-card px-3 py-2 text-dense text-paper-low"
      data-testid="traffic-refusal-summary"
    >
      <span className="font-mono text-label text-paper">网关拒绝</span>
      <span>
        {formatInteger(refused)} / {formatInteger(summary.errorCount)} errors 未拨到上游
      </span>
      <RefusalCodeBreakdown byCode={summary.refusedByCode} limit={5} />
    </div>
  );
}

function CacheWindowPanel({ windows }: { readonly windows: OpsTrafficOverviewResponse['cacheWindows'] }): JSX.Element {
  return (
    <Panel
      caption="缓存窗口"
      actions={<span className="font-mono text-micro text-paper-low">request / token</span>}
      padded
    >
      {windows === undefined || windows.length === 0 ? (
        <EmptyLine label="暂无缓存窗口数据" />
      ) : (
        <div className="flex flex-col gap-2">
          {windows.map((row) => (
            <div
              key={row.preset}
              className="grid grid-cols-[3rem_minmax(0,1fr)_auto] items-center gap-3 rounded-md bg-well px-3 py-2"
              data-testid={`traffic-cache-window-${row.preset}`}
            >
              <span className="font-mono text-label text-signal">{row.preset}</span>
              <span className="min-w-0 truncate text-dense text-paper-low">
                {formatInteger(row.cacheHitCount)} hits / {formatInteger(row.successCount)} ok
              </span>
              <span className="text-right font-mono text-dense font-semibold text-paper">
                {formatRate(row.cacheRequestHitRate)} · {formatRate(row.cacheTokenHitRate)}
              </span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function MetricCard({
  label,
  value,
  sub,
  tone = 'normal',
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly sub: string;
  readonly tone?: MetricTone;
  readonly testId?: string;
}): JSX.Element {
  return (
    <KpiCard
      label={label}
      value={value}
      tone={kpiTone(tone)}
      lamp={tone === 'critical' ? 'bad' : tone === 'warning' ? 'warn' : undefined}
      delta={{ text: sub, tone: 'neutral' }}
      testId={testId}
    />
  );
}

function TrendPanel({ buckets }: { readonly buckets: readonly OpsTrafficBucket[] }): JSX.Element {
  const rows = buckets.slice(-60);
  const maxRequests = Math.max(1, ...rows.map((row) => row.requestCount));
  const maxLatency = Math.max(1, ...rows.map((row) => row.avgFirstByteMs ?? 0));
  const width = 920;
  const height = 220;
  const paddingX = 24;
  const bottomY = 168;
  const chartHeight = 132;
  const groupWidth = Math.max(8, (width - paddingX * 2) / Math.max(1, rows.length));
  const linePoints = rows.map((row, index) => {
    const x = paddingX + groupWidth * index + groupWidth / 2;
    const y = bottomY - (((row.avgFirstByteMs ?? 0) / maxLatency) * chartHeight);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const labelEvery = Math.max(1, Math.ceil(rows.length / 7));
  return (
    <Panel
      caption="趋势"
      actions={<span className="font-mono text-micro text-paper-low">buckets {rows.length}</span>}
      padded
    >
      {rows.length === 0 ? (
        <EmptyLine label="暂无趋势数据" />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-3 text-label text-paper-low">
            <Legend className="bg-signal" label="请求" />
            <Legend className="bg-bad" label="错误" />
            <Legend className="bg-warn" label="首包" />
          </div>
          <div className="overflow-x-auto [scrollbar-gutter:stable]">
            <svg
              role="img"
              aria-label="traffic trend"
              data-testid="traffic-trend-chart"
              viewBox={`0 0 ${width} ${height}`}
              className="block h-56 min-w-[640px] rounded-lg bg-well"
            >
              <line x1={paddingX} x2={width - paddingX} y1={bottomY} y2={bottomY} strokeWidth={1} className="stroke-line" />
              {rows.map((row, index) => {
                const x = paddingX + index * groupWidth + groupWidth * 0.2;
                const barWidth = Math.max(3, groupWidth * 0.55);
                const requestHeight = Math.max(2, Math.round((row.requestCount / maxRequests) * chartHeight));
                const errorHeight = row.errorCount === 0 ? 0 : Math.max(2, Math.round((row.errorCount / maxRequests) * chartHeight));
                return (
                  <g key={`${row.bucket}-${index}`} data-testid={`traffic-trend-bucket-${index}`}>
                    <title>
                      {`${row.bucket} · ${formatInteger(row.requestCount)} requests · ${formatInteger(row.errorCount)} errors · first byte ${formatDuration(row.avgFirstByteMs)}`}
                    </title>
                    <rect x={x} y={bottomY - requestHeight} width={barWidth} height={requestHeight} rx={1} className="fill-signal" />
                    {errorHeight > 0 ? (
                      <rect x={x} y={bottomY - requestHeight} width={barWidth} height={errorHeight} rx={1} className="fill-bad" />
                    ) : null}
                    {index % labelEvery === 0 || index === rows.length - 1 ? (
                      <text x={x + barWidth / 2} y={198} textAnchor="middle" className="fill-paper-low font-mono text-micro">
                        {formatBucketLabel(row.bucket)}
                      </text>
                    ) : null}
                  </g>
                );
              })}
              {linePoints.length > 0 ? (
                <polyline points={linePoints} fill="none" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="stroke-warn" />
              ) : null}
            </svg>
          </div>
        </div>
      )}
    </Panel>
  );
}

/**
 * Traffic risk queue rendered in the unified risk shape (Epic #853 /
 * #858): these are the same threshold facts the unified aggregation
 * maps to `dimension: 'traffic'` server-side, but computed against THIS
 * page's window selector — so the dimension tag names the selected
 * window explicitly instead of the aggregation's fixed one.
 */
function RiskQueue({
  risks,
  window,
}: {
  readonly risks: readonly OpsTrafficRisk[];
  readonly window: OpsTrafficOverviewResponse['window'];
}): JSX.Element {
  const t = useT();
  const windowLabel = t('risk.window.preset', { preset: window.preset });
  return (
    <Panel
      caption="风险队列"
      actions={<span className="font-mono text-micro text-paper-low">{formatLocalTimestamp(window.refreshedAt)}</span>}
      padded
    >
      {risks.length === 0 ? (
        <EmptyLine label="当前窗口没有触发内置阈值。" />
      ) : (
        <div className="flex flex-col gap-3">
          {/* These cards and the 错误率(净) card above are DIFFERENT
              quantities off the same response, and side by side that reads
              as a bug unless it is said out loud: the gateway triages the
              fleet on the raw rate, so a window that is mostly gate
              refusals can show 严重 here while the net card reads 正常.
              Stated once for the section rather than per card — the basis
              is the same for all three kinds. */}
          <p className="m-0 rounded-md bg-well px-3 py-2 text-micro text-paper-low" data-testid="traffic-risk-basis">
            阈值来自网关 <span className="font-mono">buildRisks</span>，按<strong className="font-semibold text-paper">原始比率</strong>计算：
            分母是窗口内全部请求，错误数含网关拒绝。因此这里的判定可能与上方「错误率(净)」不一致 —— 两者口径不同，不是数据错误。
          </p>
          {risks.map((risk) => (
            <RiskItemCard
              key={risk.id}
              testId={`traffic-risk-${risk.severity}-${risk.kind.replaceAll('_', '-')}`}
              windowLabel={windowLabel}
              item={toUnifiedTrafficRisk(risk, window, t('risk.action.view_requests'))}
            />
          ))}
        </div>
      )}
    </Panel>
  );
}

/**
 * Map an `OpsTrafficRisk` onto the unified risk item shape. Severity is
 * already a subset of the unified vocabulary (`warning | critical`);
 * the detail line mirrors the server-side aggregation's format so the
 * same fact reads identically here and on `/business/risk`.
 */
function toUnifiedTrafficRisk(
  risk: OpsTrafficRisk,
  window: OpsTrafficOverviewResponse['window'],
  targetLabel: string,
): RiskCardItem {
  return {
    id: `risk_traffic_${risk.id}`,
    dimension: 'traffic',
    severity: risk.severity,
    title: risk.label,
    detail: `${formatInteger(risk.affectedCount)} / ${formatInteger(risk.requestCount)} · ${formatRate(risk.currentRate)} vs threshold ${formatRate(risk.thresholdRate)}`,
    targetLabel,
    href: drilldownHref({}, window),
    occurredAt: null,
  };
}

function DimensionWorkbench({
  data,
  tab,
  onTab,
}: {
  readonly data: OpsTrafficOverviewResponse;
  readonly tab: TrafficTab;
  readonly onTab: (tab: TrafficTab) => void;
}): JSX.Element {
  return (
    <Panel
      caption="维度视角"
      actions={
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="traffic dimensions">
          {TAB_OPTIONS.map((item) => (
            <Button
              key={item.key}
              aria-pressed={tab === item.key}
              variant={tab === item.key ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => onTab(item.key)}
              data-testid={item.testId}
            >
              {item.label}
            </Button>
          ))}
        </div>
      }
      padded
    >
      {tab === 'supply' ? <SupplyTable data={data} /> : null}
      {tab === 'customers' ? <CustomersTable data={data} /> : null}
      {tab === 'products' ? <ProductsTable data={data} /> : null}
    </Panel>
  );
}

function SupplyTable({ data }: { readonly data: OpsTrafficOverviewResponse }): JSX.Element {
  const rows = [
    ...withGroup('upstream', data.supply.upstreams),
    ...withGroup('account', data.supply.accounts),
    ...withGroup('key bucket', data.supply.keyBuckets),
    ...withGroup('upstream channel', data.supply.channels),
    // (SKU × channel) rows answer a question the channel totals above
    // cannot: how much of ONE SKU a channel carried, and how much of that
    // failed. A channel serving several SKUs can be healthy overall while
    // failing every request of one of them — its 份额 here is of that SKU.
    ...withGroup('sku × channel', data.supply.skuChannels ?? []),
  ];
  return (
    <Table testId="traffic-supply-table">
      <THead>
        <Tr interactive={false}>
          <Th>层级</Th>
          <Th>SKU</Th>
          <Th>上游</Th>
          <Th>上游账号</Th>
          <Th>Key bucket</Th>
          <Th>上游渠道</Th>
          <Th num>份额</Th>
          <Th num>请求</Th>
          <Th num>缓存命中</Th>
          <Th num>错误率(净)</Th>
          <Th num>网关拒绝</Th>
          <Th num>5xx</Th>
          <Th num>Fallback</Th>
          <Th num>首包</Th>
          <Th num>上游耗时</Th>
          <Th num>动作</Th>
        </Tr>
      </THead>
      <TBody>
        {rows.map(({ group, row }, index) => (
          <Tr key={`${group}-${rowKey(row)}-${index}`}>
            <Td><GroupChip label={group} /></Td>
            <EntityCell primary={row.skuName ?? '—'} secondary={row.skuId ?? undefined} />
            <EntityCell primary={row.upstreamName ?? row.upstreamId ?? '未知上游'} secondary={row.upstreamId ?? undefined} />
            <Td mono>{row.upstreamAccountId ?? '—'}</Td>
            <Td mono>{row.upstreamKeyBucketId ?? '—'}</Td>
            <EntityCell primary={row.upstreamChannelSlug ?? '—'} secondary={row.upstreamChannelId ?? undefined} />
            <ShareCell row={row} />
            <MetricCell>{formatInteger(row.requestCount)}</MetricCell>
            <MetricCell>{formatRate(row.cacheRequestHitRate)}</MetricCell>
            <NetErrorCell metrics={row} />
            <MetricCell>{formatRefusedCount(row)}</MetricCell>
            <MetricCell tone={row.serverErrorCount > 0 ? 'critical' : 'normal'}>{formatInteger(row.serverErrorCount)}</MetricCell>
            <MetricCell tone={fallbackTone(row.fallbackRate)}>{formatRate(row.fallbackRate)}</MetricCell>
            <MetricCell tone={row.avgFirstByteMs !== null && row.avgFirstByteMs > 10_000 ? 'critical' : 'normal'}>{formatDuration(row.avgFirstByteMs)}</MetricCell>
            <MetricCell>{formatDuration(row.avgUpstreamMs)}</MetricCell>
            <ActionCell row={row} window={data.window} />
          </Tr>
        ))}
      </TBody>
    </Table>
  );
}

function CustomersTable({ data }: { readonly data: OpsTrafficOverviewResponse }): JSX.Element {
  const rows = [
    ...withGroup('account', data.customers.accounts),
    ...withGroup('api key', data.customers.apiKeys),
    ...withGroup('member', data.customers.members),
  ];
  return (
    <Table testId="traffic-customers-table">
      <THead>
        <Tr interactive={false}>
          <Th>层级</Th>
          <Th>账户 / Org</Th>
          <Th>API Key</Th>
          <Th>Member</Th>
          <Th num>请求</Th>
          <Th num>缓存命中</Th>
          <Th num>错误</Th>
          <Th num>429</Th>
          <Th num>慢首包</Th>
          <Th num>Tokens</Th>
          <Th num>成本 (USD)</Th>
          <Th num>动作</Th>
        </Tr>
      </THead>
      <TBody>
        {rows.map(({ group, row }, index) => (
          <Tr key={`${group}-${rowKey(row)}-${index}`}>
            <Td><GroupChip label={group} /></Td>
            <CustomerAccountCell row={row} />
            <EntityCell primary={row.apiKeyName ?? row.apiKeyId ?? '—'} secondary={row.apiKeyPrefix ?? row.apiKeyId ?? undefined} />
            <EntityCell primary={row.memberEmail ?? row.memberAccountId ?? '—'} secondary={row.memberAccountId ?? undefined} />
            <MetricCell>{formatInteger(row.requestCount)}</MetricCell>
            <MetricCell>{formatRate(row.cacheRequestHitRate)}</MetricCell>
            <MetricCell tone={row.errorCount > 0 ? 'critical' : 'normal'}>{formatInteger(row.errorCount)}</MetricCell>
            <MetricCell tone={row.rateLimitedCount > 0 ? 'warning' : 'normal'}>{formatInteger(row.rateLimitedCount)}</MetricCell>
            <MetricCell tone={slowFirstByteTone(row.slowFirstByteRate)}>{formatInteger(row.slowFirstByteCount)}</MetricCell>
            <MetricCell>{formatCompact(row.inputTokens + row.outputTokens)}</MetricCell>
            <MetricCell>{formatUsdMicrosOrCents(row.costUsdMicros, row.costUsdCents, false)}</MetricCell>
            <ActionCell row={row} window={data.window} />
          </Tr>
        ))}
      </TBody>
    </Table>
  );
}

function ProductsTable({ data }: { readonly data: OpsTrafficOverviewResponse }): JSX.Element {
  const rows = [
    ...withGroup('brand', data.products.brands),
    ...withGroup('channel', data.products.channels),
    ...withGroup('sku', data.products.skus ?? []),
    ...withGroup('model', data.products.models),
    ...withGroup('route', data.products.routes),
  ];
  return (
    <Table testId="traffic-products-table">
      <THead>
        <Tr interactive={false}>
          <Th>层级</Th>
          <Th>品牌</Th>
          <Th>平台渠道</Th>
          <Th>模型 / 协议</Th>
          <Th>Route</Th>
          <Th num>份额</Th>
          <Th num>请求</Th>
          <Th num>缓存命中</Th>
          <Th num>错误率(净)</Th>
          <Th num>网关拒绝</Th>
          <Th num>缓存 tokens</Th>
          <Th num>成本 (USD)</Th>
          <Th num>动作</Th>
        </Tr>
      </THead>
      <TBody>
        {rows.map(({ group, row }, index) => (
          <Tr key={`${group}-${rowKey(row)}-${index}`}>
            <Td><GroupChip label={group} /></Td>
            <EntityCell primary={brandLabel(row.brand)} secondary={row.brand ?? undefined} />
            <EntityCell primary={row.channelName ?? row.channelSlug ?? '—'} secondary={row.channelId ?? row.channelSlug ?? undefined} />
            <EntityCell primary={row.skuName ?? row.model ?? '—'} secondary={row.skuId ?? ([row.vendor, row.protocol].filter(Boolean).join(' · ') || undefined)} />
            <EntityCell primary={row.route ?? row.lane ?? '—'} secondary={row.routeResolutionSource ?? undefined} />
            <MetricCell>{formatRate(row.requestShare)}</MetricCell>
            <MetricCell>{formatInteger(row.requestCount)}</MetricCell>
            <MetricCell>{formatRate(row.cacheRequestHitRate)}</MetricCell>
            <NetErrorCell metrics={row} />
            {/* Only `products.skus` carries the per-code map (payload), so
                the leading code shows here and nowhere else in this table. */}
            <MetricCell>
              {formatRefusedCount(row)}
              <TopRefusalCode byCode={row.refusedByCode} />
            </MetricCell>
            <MetricCell>{formatCompact(row.cacheReadTokens + row.cacheWriteTokens)}</MetricCell>
            <MetricCell>{formatUsdMicrosOrCents(row.costUsdMicros, row.costUsdCents, false)}</MetricCell>
            <ActionCell row={row} window={data.window} />
          </Tr>
        ))}
      </TBody>
    </Table>
  );
}

function EmptyLine({ label }: { readonly label: string }): JSX.Element {
  return <div className="rounded-md bg-well px-4 py-3 text-body text-paper-low">{label}</div>;
}

function CustomerAccountCell({ row }: { readonly row: OpsTrafficBreakdownRow }): JSX.Element {
  const primary = row.accountEmail ?? row.accountId ?? '未知账户';
  const secondary = row.orgId ?? row.accountId ?? undefined;
  const orgId = row.orgId ?? null;
  return (
    <Td emphasis>
      <CustomerCell
        orgId={orgId}
        accountId={orgId === null ? row.accountId : null}
        testId={row.accountId !== null && row.accountId !== undefined ? `traffic-customer-${row.accountId}` : undefined}
        secondary={
          <div>
            <div className="truncate" title={primary}>{primary}</div>
            {secondary !== undefined && secondary.length > 0 ? (
              <div className="truncate font-mono text-micro font-normal text-paper-low" title={secondary}>{secondary}</div>
            ) : null}
          </div>
        }
      />
    </Td>
  );
}

function EntityCell({
  primary,
  secondary,
}: {
  readonly primary: string;
  readonly secondary?: string;
}): JSX.Element {
  return (
    <Td emphasis>
      <div className="truncate" title={primary}>{primary}</div>
      {secondary !== undefined && secondary.length > 0 ? (
        <div className="truncate font-mono text-micro font-normal text-paper-low" title={secondary}>{secondary}</div>
      ) : null}
    </Td>
  );
}

/**
 * `requestShare`, with its denominator named when it is not the fleet.
 *
 * The supply table stacks five groups under one 份额 header, and the
 * `sku × channel` group's share is of THAT SKU while the other four are of the
 * whole window. Two numbers in one column meaning different things is a
 * misreading waiting to happen — a 43% leg and a 43% upstream are not
 * comparable quantities — and a code comment does not reach the operator.
 * Only the leg rows carry a `skuId`, which is what distinguishes them.
 */
function ShareCell({ row }: { readonly row: OpsTrafficBreakdownRow }): JSX.Element {
  const perSku = row.skuId !== null && row.skuId !== undefined;
  return (
    <Td num>
      <span className="font-semibold text-paper">{formatRate(row.requestShare)}</span>
      {perSku ? (
        <span className="block font-mono text-micro font-normal text-paper-low">占该 SKU</span>
      ) : null}
    </Td>
  );
}

function MetricCell({
  children,
  tone = 'normal',
}: {
  readonly children: ReactNode;
  readonly tone?: MetricTone;
}): JSX.Element {
  return (
    <Td num>
      <span className={`font-semibold ${toneValueClass(tone)}`}>{children}</span>
    </Td>
  );
}

function ActionCell({
  row,
  window,
}: {
  readonly row: OpsTrafficBreakdownRow;
  readonly window: OpsTrafficOverviewResponse['window'];
}): JSX.Element {
  return (
    <Td num>
      <Link to={drilldownHref(row, window)} className={buttonClass('ghost', 'sm')}>
        查看请求
        <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
      </Link>
    </Td>
  );
}

function GroupChip({ label }: { readonly label: string }): JSX.Element {
  return (
    <Chip tone="neutral" lamp={false}>
      {label}
    </Chip>
  );
}

function Legend({ className, label }: { readonly className: string; readonly label: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-2">
      <span aria-hidden="true" className={`h-2 w-2 rounded-full ${className}`} />
      <span>{label}</span>
    </span>
  );
}

function withGroup(group: string, rows: readonly OpsTrafficBreakdownRow[]): Array<{ group: string; row: OpsTrafficBreakdownRow }> {
  return rows.map((row) => ({ group, row }));
}

function drilldownHref(row: Partial<OpsTrafficBreakdownRow>, window: OpsTrafficOverviewResponse['window']): string {
  const qs = new URLSearchParams();
  qs.set('from', window.from);
  qs.set('to', window.to);
  setParam(qs, 'accountId', row.accountId);
  setParam(qs, 'apiKeyId', row.apiKeyId);
  setParam(qs, 'channelId', row.channelId);
  setParam(qs, 'channelSlug', row.channelSlug);
  setParam(qs, 'upstreamId', row.upstreamId);
  setParam(qs, 'upstreamAccountId', row.upstreamAccountId);
  setParam(qs, 'upstreamKeyBucketId', row.upstreamKeyBucketId);
  setParam(qs, 'upstreamChannelId', row.upstreamChannelId);
  setParam(qs, 'upstreamChannelSlug', row.upstreamChannelSlug);
  setParam(qs, 'lane', row.lane);
  setParam(qs, 'routeResolutionSource', row.routeResolutionSource);
  setParam(qs, 'skuId', row.skuId);
  if (row.brand === 'nadoutong' || row.brand === 'nxtpath') qs.set('brand', row.brand);
  setParam(qs, 'model', row.model);
  return `/customers/requests?${qs.toString()}`;
}

function setParam(qs: URLSearchParams, key: string, value: string | null | undefined): void {
  if (typeof value === 'string' && value.length > 0) qs.set(key, value);
}

function rowKey(row: OpsTrafficBreakdownRow): string {
  return [
    row.upstreamId,
    row.upstreamAccountId,
    row.upstreamKeyBucketId,
    row.upstreamChannelId,
    row.accountId,
    row.apiKeyId,
    row.memberAccountId,
    row.brand,
    row.channelId,
    row.skuId,
    row.vendor,
    row.model,
    row.lane,
  ].filter(Boolean).join(':') || 'summary';
}

function parseWindow(raw: string | null): OpsTrafficWindow {
  return raw === '5m' || raw === '24h' || raw === '7d' || raw === 'custom' ? raw : '1h';
}

function parseTrafficClass(raw: string | null): OpsTrafficTrafficClass {
  return raw === 'probe' || raw === 'all' ? raw : 'tenant';
}

function parseTab(raw: string | null): TrafficTab {
  return raw === 'customers' || raw === 'products' ? raw : 'supply';
}

function trafficWindowIcon(value: OpsTrafficWindow): LucideIcon {
  if (value === '5m') return Timer;
  if (value === '1h') return Clock;
  if (value === '24h') return Calendar;
  if (value === '7d') return CalendarRange;
  return CalendarClock;
}

function trafficClassLabel(value: OpsTrafficTrafficClass): string {
  if (value === 'probe') return 'Probe';
  if (value === 'all') return 'All';
  return 'Tenant';
}

function brandLabel(value: string | null | undefined): string {
  if (value === 'nxtpath') return 'Nxtpath';
  if (value === 'nadoutong') return 'Nadoutong';
  return '—';
}

const rateTone = errorSeverity;
const fallbackTone = fallbackSeverity;
const slowFirstByteTone = slowFirstByteSeverity;

/**
 * The traffic we are answerable for, and how much of it failed.
 *
 * A refused request leaves BOTH sides of the fraction. It never became our
 * responsibility, so it is neither a failure we caused nor a request we
 * served — netting only the numerator was the original defect here, and it
 * always understates: production 2026-09-01 fable-5 read 11/9,181 = 0.1%
 * where the truth is 11/2,148 = 0.5%. The distortion scales with refusal
 * share, so it is worst exactly when refusals are the story: 100 requests, 90
 * refused, 5 real failures is 5/10 = 50%, not 5/100 = 5%.
 *
 * This is the same quantity `health-perspective/model-nodes.ts` calls
 * `downstreamErrorRate` (`attributableErrors / attributableRequests`), and it
 * has to stay the same quantity — `components/trafficHealth.ts` exists so the
 * two surfaces cannot disagree about "unhealthy", and sharing thresholds does
 * not achieve that if the callers feed them different numbers.
 *
 * Deducted refusals come from the shared WHITELIST, not the structural
 * scalar. The scalar counts every failure that never dialled an upstream,
 * including `MODEL_NOT_AVAILABLE` — which never dialled one because WE could
 * not serve the request. Deducting it reports our own inability to route as
 * the gate working correctly.
 *
 * This page deducted the scalar for one review round, while 系统健康透视
 * deducted the whitelist. Same window, same response: 0.5% here against 2.0%
 * there, each pinned by its own test, with this file's own comment asserting
 * they could not diverge. `attributableFigures` is now the single arithmetic
 * both call, so the only way to disagree again is to stop calling it.
 *
 * ── And what may honestly be PRINTED, which is not always one number ──
 *
 * Where the code split is missing the value degrades to the raw rate, and
 * that value is right — but the column is headed 「错误率(净)」 and the very
 * next column prints the un-deducted refusal count. The gateway
 * carries `refusedByCode` on exactly two scopes (the window summary and
 * `products.skus`), so on the other twelve breakdown groups that pairing read
 * as "43.0%, net of these 7,099 refusals" when the truth on that production
 * row was 17/9,467 = 0.18% — a 240x gap between the label and the number,
 * graded 严重, on the very channel this counter was added for.
 *
 * Unifying the arithmetic (`attributableFigures`) did not unify the CLAIM.
 * That is the same defect one layer up: a real number under the wrong
 * subject, which is what `components/trafficHealth.ts` exists to prevent.
 *
 * So: where the split is missing but the scalar says refusals happened, the
 * deduction is somewhere in `[0, refused]`. `(e-x)/(r-x)` is decreasing in
 * `x` (for `e < r`), so the raw rate is the UPPER bound and the
 * fully-deducted rate is the lower one. Print the bound as a bound, and grade
 * on the LOWER one — see the comment at the return, which is where a first
 * draft of this got it wrong by grading a straddling range `normal` and so
 * printing a verdict beneath the floor it had just proved.
 *
 * `refused === 0` is a reported zero: nothing to deduct, so raw IS net, exact.
 * `refused === undefined` is a gateway that cannot report the counter at all;
 * that degrades to the raw rate exactly as this page behaved before the
 * counter existed, and `formatRefusedCount` discloses it with an em dash.
 */
interface NetErrorDisplay {
  readonly text: string;
  readonly note: string | null;
  readonly tone: MetricTone;
}

function netErrorDisplay(metrics: OpsTrafficMetrics): NetErrorDisplay {
  const exact = attributableFigures(metrics.requestCount, metrics.errorCount, metrics.refusedByCode);
  if (exact.rate !== null && exact.errors !== null && exact.requests !== null) {
    return { text: formatRate(exact.rate), note: null, tone: rateTone(exact.rate, exact.errors, exact.requests) };
  }
  const rawRate = metrics.requestCount > 0 ? metrics.errorCount / metrics.requestCount : 0;
  const rawTone = rateTone(rawRate, metrics.errorCount, metrics.requestCount);
  const refused = metrics.refusedBeforeUpstreamCount;
  if (refused === undefined || refused === 0) {
    return { text: formatRate(rawRate), note: null, tone: rawTone };
  }
  const floorRequests = Math.max(0, metrics.requestCount - refused);
  const floorErrors = Math.max(0, metrics.errorCount - refused);
  const floorRate = floorRequests > 0 ? floorErrors / floorRequests : 0;
  const floorTone = rateTone(floorRate, floorErrors, floorRequests);
  // `floorTone`, NOT a fall-back-to-normal. `errorSeverity`'s three inputs
  // are each monotone non-increasing in the deduction (rate, count and total
  // all shrink), and both of its arms are upward-closed on them, so the grade
  // is monotone too: `rawTone >= floorTone` always, and `floorTone` is the
  // MINIMUM over the whole interval. Every possible x therefore grades at
  // least this badly, which makes it the strongest claim the evidence
  // supports — and, unlike a blanket `normal`, it cannot sit below its own
  // proven bound. A 500/60/45 channel floors at 15/455 = 3.3% (warning) with
  // a 12% ceiling (critical); grading that `normal` would have hidden a
  // warning we had already proved.
  return { text: `≤ ${formatRate(rawRate)}`, note: '该层无拒绝明细，未扣减', tone: floorTone };
}

/**
 * The same figure as a KPI card. The summary scope always carries the code
 * split in practice, so the caveat arm is unreachable there today — it is
 * written anyway because "in practice" is how the twelve breakdown groups got
 * their wrong label, and a card is the one place an operator reads without
 * the neighbouring refusal column to correct them.
 */
function NetErrorCard({ summary }: { readonly summary: OpsTrafficMetrics }): JSX.Element {
  const display = netErrorDisplay(summary);
  const sub = errorSubLabel(summary);
  return (
    <MetricCard
      label="错误率(净)"
      value={display.text}
      sub={display.note === null ? sub : `${sub} · ${display.note}`}
      tone={display.tone}
      testId="traffic-net-error-rate"
    />
  );
}

/** The 错误率(净) cell, with its caveat where the deduction could not be done. */
function NetErrorCell({ metrics }: { readonly metrics: OpsTrafficMetrics }): JSX.Element {
  const display = netErrorDisplay(metrics);
  return (
    <Td num>
      <span className={`font-semibold ${toneValueClass(display.tone)}`}>{display.text}</span>
      {display.note === null ? null : (
        <span className="block font-mono text-micro font-normal text-paper-low">{display.note}</span>
      )}
    </Td>
  );
}

function formatRefusedCount(metrics: OpsTrafficMetrics): string {
  const refused = metrics.refusedBeforeUpstreamCount;
  return refused === undefined ? '—' : formatInteger(refused);
}

function rateLimitedSubLabel(metrics: OpsTrafficMetrics): string {
  const absorbed = metrics.upstreamAttemptRateLimitedCount;
  if (absorbed === undefined) return 'rate limited';
  return `rate limited · 上游 429 已兜底 ${formatInteger(absorbed)}`;
}

function errorSubLabel(metrics: OpsTrafficMetrics): string {
  const refused = metrics.refusedBeforeUpstreamCount;
  if (refused === undefined) return `${formatInteger(metrics.errorCount)} errors`;
  return `${formatInteger(metrics.errorCount)} errors · 网关拒绝 ${formatInteger(refused)}`;
}

/**
 * The refusal codes behind the count, biggest first.
 *
 * The count alone is not enough to act on: `REQUEST_NOT_SUPPORTED` is a client
 * integration problem, `INSUFFICIENT_BALANCE` is revenue, and
 * `MODEL_NOT_AVAILABLE` is us failing to route a request we accepted. This
 * page does not grade them — it names them, which is what would have made the
 * 2026-09-01 fable-5 window obvious at a glance.
 *
 * The key space is open, so unknown codes render as themselves rather than
 * being folded away.
 */
function topRefusalCodes(
  byCode: Readonly<Record<string, number>> | undefined,
  limit: number,
): Array<readonly [string, number]> {
  if (byCode === undefined) return [];
  return Object.entries(byCode)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit);
}

/** The single dominant refusal code for one row, or nothing to say. */
function TopRefusalCode({
  byCode,
}: {
  readonly byCode: Readonly<Record<string, number>> | undefined;
}): JSX.Element | null {
  const top = topRefusalCodes(byCode, 1)[0];
  if (top === undefined) return null;
  return (
    <span className="block truncate font-mono text-micro font-normal text-paper-low" title={top[0]}>
      {top[0]}
    </span>
  );
}

function RefusalCodeBreakdown({
  byCode,
  limit = 4,
}: {
  readonly byCode: Readonly<Record<string, number>> | undefined;
  readonly limit?: number;
}): JSX.Element | null {
  const codes = topRefusalCodes(byCode, limit);
  if (codes.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="traffic-refusal-codes">
      {codes.map(([code, count]) => (
        <Chip key={code} tone="neutral" lamp={false}>
          <span className="font-mono">{code}</span>
          <span className="text-paper-low">{formatInteger(count)}</span>
        </Chip>
      ))}
    </div>
  );
}

function kpiTone(tone: MetricTone): KpiTone {
  if (tone === 'critical') return 'bad';
  if (tone === 'warning') return 'warn';
  return 'default';
}

function toneValueClass(tone: MetricTone): string {
  if (tone === 'critical') return 'text-bad';
  if (tone === 'warning') return 'text-warn';
  return 'text-paper';
}

function formatInteger(value: number): string {
  return INTEGER_FORMATTER.format(value);
}

function formatRate(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

function formatDuration(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(2)}s`;
  return `${Math.round(value)}ms`;
}

function formatRatio(value: number | null): string {
  return value === null || !Number.isFinite(value) ? '—' : `${value.toFixed(1)}x`;
}

function formatBucketLabel(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value.slice(5);
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return value.slice(0, 10);
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
